import React, { ReactNode } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  CircularProgress,
  Alert,
  useTheme,
} from '@mui/material';
import { SAP_COLORS, SAP_TYPOGRAPHY } from '../../theme/sapDesignSystem';

export interface DrillDownTableColumn<T = unknown> {
  id: string;
  label: string;
  align?: 'left' | 'center' | 'right';
  /** Custom cell renderer. Receives row object. */
  render?: (row: T) => ReactNode;
  /** Minimum width (e.g. '80px') */
  minWidth?: string;
}

export interface DrillDownTablePagination {
  page: number; // 0-based
  rowsPerPage: number;
  rowCount: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  rowsPerPageOptions?: number[];
}

export interface DrillDownTableProps<T = unknown> {
  /** Column definitions */
  columns: DrillDownTableColumn<T>[];
  /** Data rows */
  rows: T[];
  /** Unique row key for React list */
  getRowId: (row: T) => string | number;
  /** Loading state */
  loading?: boolean;
  /** Error message; when set, table body is replaced by error alert */
  error?: string | null;
  /** Pagination config. When omitted, no pagination is shown. */
  pagination?: DrillDownTablePagination;
  /** Called when a row is clicked (for drill-down). Optional. */
  onRowClick?: (row: T) => void;
  /** Message when rows are empty and not loading */
  emptyMessage?: string;
  /** Optional: Function to get status color for left border indicator. Returns theme color name (e.g., 'success', 'warning', 'error') */
  getRowStatusColor?: (row: T) => string | undefined;
}

/**
 * Standard table for list pages with optional drill-down and pagination.
 * Use with URL state (e.g. useSearchParams) in the parent to preserve page/filters on back.
 */
function DrillDownTable<T>({
  columns,
  rows,
  getRowId,
  loading = false,
  error = null,
  pagination,
  onRowClick,
  emptyMessage = 'No data found.',
  getRowStatusColor,
}: DrillDownTableProps<T>) {
  const theme = useTheme();
  const rowsPerPageOptions = pagination?.rowsPerPageOptions ?? [5, 10, 25, 50];

  const getStatusBorderColor = (statusColor?: string): string => {
    if (!statusColor) return 'transparent';
    const colorMap: Record<string, string> = {
      success: theme.palette.success?.main || '#2e7d32',
      warning: theme.palette.warning?.main || '#ed6c02',
      error: theme.palette.error?.main || '#d32f2f',
      info: theme.palette.info?.main || '#0288d1',
    };
    return colorMap[statusColor] || 'transparent';
  };

  return (
    <>
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {typeof error === 'string' ? error : (error as Error)?.message ?? 'Something went wrong'}
        </Alert>
      )}

      {!loading && !error && (
        <>
          <TableContainer
            component={Paper}
            sx={{
              mb: 2,
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
              border: `1px solid ${SAP_COLORS.sapGray2}`,
            }}
          >
            <Table size="medium" stickyHeader>
              <TableHead>
                <TableRow sx={{ bgcolor: SAP_COLORS.sapLightBlue, '& th': { bgcolor: SAP_COLORS.sapLightBlue } }}>
                  {columns.map((col) => (
                    <TableCell
                      key={col.id}
                      align={col.align ?? 'left'}
                      sx={{
                        fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold,
                        color: SAP_COLORS.sapGray7,
                        minWidth: col.minWidth,
                        py: 1.5,
                        px: 2,
                        borderBottom: `2px solid ${SAP_COLORS.sapGray2}`,
                      }}
                    >
                      {col.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={columns.length} align="center" sx={{ py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        {emptyMessage}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row, index) => {
                    const statusColor = getRowStatusColor?.(row);
                    const borderColor = getStatusBorderColor(statusColor);
                    
                    return (
                      <TableRow
                        key={getRowId(row)}
                        hover={!!onRowClick}
                        onClick={onRowClick ? () => onRowClick(row) : undefined}
                        sx={{
                          cursor: onRowClick ? 'pointer' : 'default',
                          bgcolor: index % 2 === 0 ? 'transparent' : 'rgba(0, 0, 0, 0.02)',
                          transition: 'background-color 0.2s ease',
                          '&:hover': onRowClick 
                            ? { 
                                bgcolor: SAP_COLORS.hover,
                                transform: 'scale(1.001)',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                              } 
                            : undefined,
                          position: 'relative',
                          '&::before': statusColor
                            ? {
                                content: '""',
                                position: 'absolute',
                                left: 0,
                                top: 0,
                                bottom: 0,
                                width: '2px',
                                bgcolor: borderColor,
                                zIndex: 1,
                              }
                            : undefined,
                        }}
                      >
                      {columns.map((col) => (
                        <TableCell
                          key={col.id}
                          align={col.align ?? 'left'}
                          sx={{ 
                            color: SAP_COLORS.sapGray7, 
                            minWidth: col.minWidth,
                            py: 1.5,
                            px: 2,
                            borderBottom: `1px solid ${SAP_COLORS.sapGray2}`,
                            fontSize: '0.875rem',
                          }}
                        >
                          {col.render ? col.render(row) : (row as Record<string, unknown>)[col.id] as ReactNode}
                        </TableCell>
                      ))}
                    </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {pagination && rows.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Showing{' '}
                  {pagination.page * pagination.rowsPerPage + 1}-
                  {Math.min(
                    pagination.page * pagination.rowsPerPage + rows.length,
                    pagination.rowCount
                  )}{' '}
                  of {pagination.rowCount}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Rows per page</InputLabel>
                  <Select
                    value={pagination.rowsPerPage}
                    onChange={(e: SelectChangeEvent<number>) =>
                      pagination.onRowsPerPageChange(Number(e.target.value))
                    }
                    label="Rows per page"
                  >
                    {rowsPerPageOptions.map((n) => (
                      <MenuItem key={n} value={n}>
                        {n}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Pagination
                  count={Math.ceil(pagination.rowCount / pagination.rowsPerPage)}
                  page={pagination.page + 1}
                  onChange={(_, p) => pagination.onPageChange(p - 1)}
                  color="primary"
                  showFirstButton
                  showLastButton
                />
              </Box>
            </Box>
          )}
        </>
      )}
    </>
  );
}

export default DrillDownTable;
